import {
  loginIcon,
  eyeIcon,
  exportToPlus,
  LibraryIcon,
  usersIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu, useExcalidrawAPI } from "@excalidraw/excalidraw/index";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import React from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { LanguageList } from "../app-language/LanguageList";
import { AuthModal } from "./AuthModal";
import { useAuth } from "../hooks/useAuth";
import { useDrawings } from "../hooks/useDrawings";
import { DrawingsModal } from "./DrawingsModal";
import { LocalData } from "../data/LocalData";

import { saveDebugState } from "./DebugCanvas";

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  refresh: () => void;
}> = React.memo((props) => {
  const { t } = useI18n();
  const { user, token, login, logout } = useAuth();
  const { saveDrawing } = useDrawings();
  const [showAuthModal, setShowAuthModal] = React.useState(false);
  const [showDrawingsModal, setShowDrawingsModal] = React.useState(false);
  const [currentDrawingId, setCurrentDrawingId] = React.useState<number | null>(null);
  const excalidrawAPI = useExcalidrawAPI();

  const handleSaveToServer = async () => {
    if (!excalidrawAPI || !user) return;
    try {
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();
      const currentName = excalidrawAPI.getName() || `Dibujo de ${user.username}`;
      const name = window.prompt(
        currentDrawingId 
          ? "Ingresa el nombre del dibujo (Si cambias el nombre, se actualizará el dibujo existente):"
          : "Ingresa un nombre para el nuevo dibujo:", 
        currentName
      );
      
      if (name === null) return; // User cancelled
      
      let isUpdate = false;
      if (currentDrawingId) {
         isUpdate = window.confirm("¿Deseas sobrescribir/actualizar el dibujo actual en el servidor? (Cancela para guardarlo como un dibujo nuevo)");
      }
      
      const savedName = name.trim() || currentName;
      
      // Update local Excalidraw state immediately with the new name
      excalidrawAPI.updateScene({
        appState: {
          name: savedName,
        },
      });

      // Save and flush to local storage immediately to prevent focus/blur sync from reverting name
      const files = excalidrawAPI.getFiles();
      LocalData.save(elements, { ...appState, name: savedName }, files, () => {});
      LocalData.flushSave();
      
      const result = await saveDrawing({
        id: isUpdate && currentDrawingId ? currentDrawingId : undefined,
        name: savedName,
        data: { elements, appState: { ...appState, name: savedName } },
      });
      
      if (!isUpdate && result && result.drawing && result.drawing.id) {
        setCurrentDrawingId(result.drawing.id);
      }

      excalidrawAPI.setToast({ message: isUpdate ? "Dibujo actualizado en el servidor!" : "Nuevo dibujo guardado en el servidor!" });
    } catch (err: any) {
      excalidrawAPI.setToast({ message: `Error: ${err.message}` });
    }
  };

  return (
    <>
      <MainMenu>
        <MainMenu.DefaultItems.LoadScene />
        <MainMenu.DefaultItems.SaveToActiveFile />
        <MainMenu.DefaultItems.Export />
        <MainMenu.DefaultItems.SaveAsImage />
        {props.isCollabEnabled && (
          <MainMenu.DefaultItems.LiveCollaborationTrigger
            isCollaborating={props.isCollaborating}
            onSelect={() => props.onCollabDialogOpen()}
          />
        )}
        <MainMenu.DefaultItems.CommandPalette className="highlighted" />
        <MainMenu.DefaultItems.SearchMenu />
        <MainMenu.DefaultItems.Help />
        <MainMenu.DefaultItems.ClearCanvas />
        <MainMenu.Separator />
        {user ? (
          <>
            <MainMenu.Item
              onSelect={() => {
                if (window.confirm("¿Deseas iniciar un dibujo nuevo? Se limpiará el lienzo.")) {
                  excalidrawAPI?.resetScene();
                  setCurrentDrawingId(null);
                  excalidrawAPI?.setToast({ message: "Lienzo restablecido. Guardar creará un nuevo dibujo." });
                }
              }}
            >
              Nuevo Dibujo
            </MainMenu.Item>
            <MainMenu.Item
              icon={exportToPlus}
              onSelect={handleSaveToServer}
            >
              Guardar en Servidor
            </MainMenu.Item>
            <MainMenu.Item
              icon={LibraryIcon}
              onSelect={() => setShowDrawingsModal(true)}
            >
              Cargar de Servidor
            </MainMenu.Item>
            {user.username === "admin" && (
              <MainMenu.Item
                icon={usersIcon}
                onSelect={() => setShowAuthModal(true)}
              >
                Crear Usuario
              </MainMenu.Item>
            )}
            <MainMenu.Item
              icon={loginIcon}
              onSelect={() => {
                logout();
                setCurrentDrawingId(null);
              }}
              className="highlighted"
            >
              {t("auth.logout")} ({user.username})
            </MainMenu.Item>
          </>
        ) : (
          <MainMenu.Item
            icon={loginIcon}
            onSelect={() => setShowAuthModal(true)}
            className="highlighted"
          >
            {t("auth.login")}
          </MainMenu.Item>
        )}
        {isDevEnv() && (
          <MainMenu.Item
            icon={eyeIcon}
            onSelect={() => {
              if (window.visualDebug) {
                delete window.visualDebug;
                saveDebugState({ enabled: false });
              } else {
                window.visualDebug = { data: [] };
                saveDebugState({ enabled: true });
              }
              props?.refresh();
            }}
          >
            Visual Debug
          </MainMenu.Item>
        )}
        <MainMenu.Separator />
        <MainMenu.DefaultItems.Preferences />
        <MainMenu.DefaultItems.ToggleTheme allowSystemTheme theme={props.theme} />
        <MainMenu.ItemCustom>
          <LanguageList style={{ width: "100%" }} />
        </MainMenu.ItemCustom>
        <MainMenu.DefaultItems.ChangeCanvasBackground />
      </MainMenu>
      
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={login}
          currentUser={user && token ? { ...user, token } : null}
        />
      )}
      {showDrawingsModal && (
        <DrawingsModal
          isOpen={showDrawingsModal}
          onClose={() => setShowDrawingsModal(false)}
          onLoadDrawing={(id) => setCurrentDrawingId(id)}
        />
      )}
    </>
  );
});